import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // CHECK for SSO token on mount (URL parameter or localStorage)
  useEffect(() => {
    checkSSOToken();
  }, []);

  // CHECK for SSO token in URL or localStorage
  const checkSSOToken = async () => {
    try {
      // CHECK URL parameters for sso_token
      const urlParams = new URLSearchParams(window.location.search);
      const ssoToken = urlParams.get('sso_token');

      if (ssoToken) {
        console.log('🔑 SSO token detected in URL, validating...');
        await validateToken(ssoToken);

        // CLEAN URL to remove token from browser history
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        // CHECK localStorage for existing token
        const storedToken = localStorage.getItem('auth_token');
        if (storedToken) {
          console.log('🔑 Stored token found, validating...');
          await validateToken(storedToken);
        }
      }
    } catch (error) {
      console.warn('⚠️ SSO validation failed, continuing as anonymous:', error.message);
      // CLEAR invalid token from storage
      localStorage.removeItem('auth_token');
    } finally {
      setLoading(false);
    }
  };

  // VALIDATE token against backend
  const validateToken = async (tokenValue) => {
    try {
      // Use correct API URL for both dev and production
      const apiBaseUrl = process.env.NODE_ENV === 'production'
        ? 'https://api.cipher.insurtale.io/api'
        : '/api';

      const response = await axios.post(`${apiBaseUrl}/auth/validate-token`, {
        token: tokenValue,
      });

      if (response.data.success) {
        setToken(tokenValue);
        setUser(response.data.user);
        setIsAuthenticated(true);
        localStorage.setItem('auth_token', tokenValue);
        console.log('✅ Authenticated as:', response.data.user.username);
      } else {
        throw new Error('Token validation failed');
      }
    } catch (error) {
      // Token is invalid or expired
      throw new Error(error.response?.data?.error || 'Token validation failed');
    }
  };

  // HANDLE user logout
  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('auth_token');
    console.log('👋 Logged out, returning to anonymous mode');
  };

  // GET current user's JWT token for API requests
  const getToken = () => token;

  const value = {
    isAuthenticated,
    user,
    loading,
    token,
    logout,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};